// PageShell — responsive backdrop + brand block + card slot for single-
// task screens (login, password reset, MFA enroll, device pair claim).
//
// Layout policy: single column at every breakpoint. Mobile is
// edge-padded; tablet/desktop center the column with comfortable
// whitespace. No two-column hero — the product is a single-action
// screen, not a marketing page; centering keeps focus on the form.
//
//   mobile  : 380px max, edge-padded, brand near top
//   tablet  : 440px max, centered, more head-room
//   desktop : 440px max, centered, generous top padding
//
// Backdrop: clean off-white with a faint radial accent in the top-left.
// Soft, modern, not "tech dashboard" slate.

import type { ReactNode } from "react";

import { tokens } from "../tokens/tokens";

export interface PageShellProps {
  /** Big text under the brand mark. Required. */
  title: string;
  /** Smaller line under the title. Optional. */
  tagline?: string;
  /** Brand mark element rendered above the title. */
  brand?: ReactNode;
  /** Below-card content — trust signal, support contact, etc. */
  footer?: ReactNode;
  /** The main content (a Card + form). */
  children: ReactNode;
}

export function PageShell({
  title,
  tagline,
  brand,
  footer,
  children,
}: PageShellProps) {
  return (
    <>
      <style>{shellCSS}</style>
      <main className="ds-shell">
        <div className="ds-shell__bg" aria-hidden="true" />
        <div className="ds-shell__main">
          <header className="ds-shell__header">
            {brand && (
              <div className="ds-shell__brand" aria-hidden="true">
                {brand}
              </div>
            )}
            <h1 className="ds-shell__title">{title}</h1>
            {tagline && <p className="ds-shell__tagline">{tagline}</p>}
          </header>
          <div className="ds-shell__content">{children}</div>
          {footer && <div className="ds-shell__footer">{footer}</div>}
        </div>
      </main>
    </>
  );
}

const shellCSS = `
.ds-shell {
  position: relative;
  min-height: 100dvh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  padding: max(${tokens.space[6]}, 5vh) ${tokens.space[4]} ${tokens.space[8]};
  /* Clean warm off-white. Resists the SaaS-template radial-gradient
   * temptation. Card carries the visual interest, not the page. */
  background: #fbfaf7;
  overflow: hidden;
  isolation: isolate;
}
.ds-shell__bg {
  position: absolute;
  inset: 0;
  z-index: -1;
  /* Single tonal stop, very subtle. Disappears on most screens but
   * keeps the page from being clinically flat. */
  background: linear-gradient(180deg, #fbfaf7 0%, #ffffff 80%);
}
.ds-shell__main {
  width: 100%;
  max-width: 400px;
  display: flex;
  flex-direction: column;
  gap: ${tokens.space[6]};
  position: relative;
  z-index: 1;
  animation: dsShellEnter 360ms cubic-bezier(0.16, 1, 0.3, 1) both;
}
@keyframes dsShellEnter {
  from { opacity: 0; transform: translateY(8px); }
  to   { opacity: 1; transform: translateY(0); }
}
@media (prefers-reduced-motion: reduce) {
  .ds-shell__main { animation: none; }
}
.ds-shell__header {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${tokens.space[2]};
  text-align: center;
}
.ds-shell__brand { margin-bottom: ${tokens.space[2]}; }
.ds-shell__title {
  font-size: clamp(22px, 5vw, 28px);
  font-weight: ${tokens.type.weight.bold};
  color: ${tokens.color.semantic.heading};
  letter-spacing: -0.022em;
  word-break: keep-all;
  line-height: 1.2;
}
.ds-shell__tagline {
  font-size: ${tokens.type.size.sm};
  color: ${tokens.color.semantic.textMuted};
  max-width: 36ch;
  line-height: 1.5;
}
.ds-shell__content { width: 100%; }
.ds-shell__footer {
  text-align: center;
  font-size: ${tokens.type.size.xs};
  color: ${tokens.color.semantic.textMuted};
  line-height: 1.5;
  max-width: 36ch;
  margin: 0 auto;
}

@media (min-width: 640px) {
  .ds-shell {
    padding: ${tokens.space[10]} ${tokens.space[6]} ${tokens.space[8]};
  }
  .ds-shell__main { max-width: 440px; gap: ${tokens.space[7]}; }
}

@media (min-width: 1024px) {
  .ds-shell { padding-top: ${tokens.space[16]}; }
}
`;
